import React, { useEffect, useState } from 'react';
import styled from "styled-components";
import { connect } from 'react-redux';
import { getQuotationById, getMaterialsBySubquotationId, getSubQuotationsPostulation, deleteQuotation, openMainModal, PauseQuotation } from '../../../store/actions/profileActions';
import styles from './QuotationDetails.module.scss';
import QuotationForm from '../CotizadorDeMateriales/Quotationform';
import MaterialTable from '../CotizadorDeMateriales/MaterialTable';
import { useNavigate, useParams } from 'react-router-dom';
import NewNavImproved from '../../Nav/NewNavImproved.tsx';
import BotoneraModern from '../../Botonera/BotoneraModern.tsx';
import { Inbox, Pause, Pencil, Play, Trash2, Download, X } from 'lucide-react';
import RegisteredUserProfileLoader from '../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader';
import Breadcrumbs from '../../UI/Breadcrumbs/Breadcrumbs.tsx';
import useNavVisibility from '../../../hooks/useNavVisibility.ts';
import ModalOverlay from '../../UI/ModalOverlay/ModalOverlay.tsx';

const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin-bottom: 10px;
  padding-right: 5%;
  padding-left: 5%;
  max-width: 150rem;
        margin: auto;

  @media (max-width: 1200px) {
    padding-right: 5%;
    padding-left: 5%;
    }

  @media (max-width: 900px) {
    padding-right: 0rem;
    padding-left: 0rem;
  }

  @media (max-width: 600px) {
    padding-right: 0;
    padding-left: 0;
  }
  `;

const QuotationDetails = ({
  quotationData,
  materialsBySubquotation,
  getQuotationById,
  getMaterialsBySubquotationId,
  loading,
  getSubQuotationsPostulation,
  subQuotationsPostulations,
  deleteQuotation,
  openMainModal,
  PauseQuotation,
}) => {
  const [materials, setMaterials] = useState([]);
  const [receivedQuotes, setReceivedQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const quotationId = quotationData?.id;
  const subquotationId = quotationData?.subquotation?.[0]?.id;

  useEffect(() => {
    if (id && (!quotationId || quotationId !== id || shouldRefresh)) {
      getQuotationById(id, false, null);
      setShouldRefresh(false);
    }
  }, [id, quotationId, shouldRefresh, getQuotationById]);

  useEffect(() => {
    if (subquotationId) {
      Promise.all([
        getMaterialsBySubquotationId(subquotationId),
        getSubQuotationsPostulation(subquotationId),
      ]).finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [subquotationId, getMaterialsBySubquotationId, getSubQuotationsPostulation]);


  useEffect(() => {
    if (materialsBySubquotation) {
      const transformedMaterials = Object.values(materialsBySubquotation).flat();
      setMaterials(transformedMaterials.map((category) => ({
        category_id: category.category_id,
        name: category.name,
        materials: category.materials,
      })));
    }

    if (subQuotationsPostulations) {
      setReceivedQuotes(subQuotationsPostulations);
    }
  }, [materialsBySubquotation, subQuotationsPostulations]);


  const handleEditClick = () => {
    navigate(`/cotizaciondemateriales/edit/${id}`);
  };

  const handleDeleteQuotation = (id) => {
    openMainModal({
      title: "¿Estás seguro de querer eliminar la cotización?",
      body: "Si la eliminas no podrás recuperarla.",
      isSuccess: false,
      iconType: "warning",
      confirmHandler: () => {
        deleteQuotation(id, navigate);
      }
    });
  };

  const handleToggleQuotation = (id, isActive) => {
    const action = isActive ? "reactivar" : "pausar";
    const title = `¿Estás seguro de querer ${action} la cotización?`;
    const body = isActive
      ? "Si reactivas la cotización, estará disponible para los usuarios."
      : "Si pausas la cotización, no podrá ser vista por los usuarios de Edify.";

    openMainModal({
      title,
      body,
      isSuccess: false,
      iconType: "warning",
      confirmHandler: async () => {
        await PauseQuotation(id, isActive); 
        setShouldRefresh(true); 
      },
    });
  };

  const handleDownload = (fileUrl) => {
    if (!fileUrl) {
      openMainModal({
        title: "Error",
        body: "El archivo no está disponible.",
        isSuccess: false,
        iconType: "error",
      });
      return;
    }
    window.open(fileUrl, '_blank');
  };

  const formatDate = (date) => {
    if (!date) return '';
    const utcDate = new Date(date);
    if (Number.isNaN(utcDate.getTime())) return '';
    const year = utcDate.getUTCFullYear();
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getUTCDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  const quotation = quotationData?.quotation;
  const hasBudgets = receivedQuotes.length > 0;

  if (loading) return <p>Cargando...</p>;

  const isPaused = !quotation?.active;

  return (
    <Container>
      {!ocultarEnMobile && <NewNavImproved />}
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      {isLoading ? (
        <div className={styles.loaderContainer}>
          <RegisteredUserProfileLoader />
        </div>
      ) : (
        <div className={styles.page}>
          <div className={styles.breadcrumbContainer}>
            <Breadcrumbs />
          </div>

          <section className={styles.hero}>
            <div className={styles.heroPrimary}>
              <div className={styles.heroHeader}>
                <span className={styles.heroBadge}>Cotización de materiales</span>
                {isPaused && (
                  <span className={styles.pausedTag}>
                    <Pause size={14} />
                    Cotización pausada
                  </span>
                )}
              </div>
              <h1 className={styles.heroTitle}>{quotation?.name || 'Cotización sin nombre'}</h1>
            </div>
            <aside className={styles.heroActions}>
              <button
                type="button"
                className={`${styles.managementButton} ${styles.managementButtonSecondary}`}
                onClick={handleEditClick}
              >
                <Pencil size={16} />
                <span>Editar</span>
              </button>
              <button
                type="button"
                className={`${styles.managementButton} ${styles.managementButtonSecondary}`}
                onClick={() => handleToggleQuotation(id, isPaused)}
              >
                {isPaused ? <Play size={16} /> : <Pause size={16} />}
                <span>{isPaused ? 'Reactivar' : 'Pausar'}</span>
              </button>
              <button
                type="button"
                className={`${styles.managementButton} ${styles.managementButtonDanger}`}
                onClick={() => handleDeleteQuotation(id)}
              >
                <Trash2 size={16} />
                <span>Eliminar</span>
              </button>
              <button
                type="button"
                className={`${styles.managementButton} ${styles.managementButtonPrimary}`}
                onClick={() => setShowBudgetModal(true)}
              >
                <Inbox size={16} />
                <span>Presupuestos</span>
                {hasBudgets && <span className={styles.managementCounter}>{receivedQuotes.length}</span>}
              </button>
            </aside>
          </section>

          <div className={styles.layout}>
            <div className={styles.primaryColumn}>
              <section className={styles.card}>
                <QuotationForm quotation={quotationData} />
              </section>
              <section className={styles.card}>
                <MaterialTable
                  materials={materials}
                  onMaterialsChange={() => {}}
                  setShowSummary={() => {}}
                  isEditable={false}
                  isMyQuotation
                  totals={0}
                />
              </section>
            </div>
          </div>
        </div>
      )}

      <ModalOverlay isOpen={showBudgetModal} onClose={() => setShowBudgetModal(false)} zIndex={25}>
        <div className={styles.budgetModalContent}>
          <div className={styles.budgetHeader}>
            <h3 className={styles.budgetTitle}>
              Presupuestos recibidos
              {hasBudgets && <span>{` (${receivedQuotes.length})`}</span>}
            </h3>
            <button
              type="button"
              className={styles.budgetClose}
              onClick={() => setShowBudgetModal(false)}
              aria-label="Cerrar presupuestos"
            >
              <X size={16} />
            </button>
          </div>

          {hasBudgets ? (
            <div className={styles.budgetList}>
              {receivedQuotes.map((quote, idx) => {
                const { file, user, end_date } = quote;
                const displayName =
                  user?.type === 'COMPANY'
                    ? user?.razon_social
                    : [user?.name, user?.last_name].filter(Boolean).join(' ') || 'Usuario sin datos';
                const initials = (displayName || '')
                  .split(' ')
                  .filter(Boolean)
                  .map((segment) => segment[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase();

                return (
                  <div key={idx} className={styles.budgetItem}>
                    <div className={styles.budgetAvatar}>
                      {user?.image_profile ? (
                        <img src={user.image_profile} alt={displayName} />
                      ) : (
                        <span>{initials || 'ED'}</span>
                      )}
                    </div>
                    <div className={styles.budgetInfo}>
                      <span className={styles.budgetName}>{displayName}</span>
                      <span className={styles.budgetMeta}>
                        Presupuesto válido hasta {formatDate(end_date)}
                      </span>
                      <button
                        type="button"
                        className={styles.budgetDownload}
                        onClick={() => handleDownload(file)}
                      >
                        <Download size={14} />
                        Descargar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.budgetEmpty}>
              <Inbox size={32} />
              <p>No tienes presupuestos recibidos.</p>
            </div>
          )}
        </div>
      </ModalOverlay>
    </Container>
  );
};


const mapStateToProps = (state) => ({
  quotationData: state.profile.quotation,
  materialsBySubquotation: state.profile.materialsBySubquotation,
  loading: state.profile.loading,
  error: state.profile.error,
  userData: state.general.userData,
  subQuotationsPostulations: state.profile.subQuotationsPostulations,
});

const mapDispatchToProps = {
  getQuotationById,
  getMaterialsBySubquotationId,
  getSubQuotationsPostulation,
  deleteQuotation,
  openMainModal,
  PauseQuotation
};

export default connect(mapStateToProps, mapDispatchToProps)(QuotationDetails);
